import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  monthly_fee: 'Forfait mensuel',
  backlinks: 'Backlinks (ponctuel)',
  website: 'Création / refonte site',
  other: 'Autre prestation',
};

export default function FinanceInvoiceDialog({ clients, defaultMonth, onClose, onSaved }) {
  const [form, setForm] = useState({
    client_id: clients[0]?.client_id || '',
    month: defaultMonth,
    type: 'website',
    label: '',
    amount: 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.client_id) return toast.error("Sélectionnez un client");
    if (!form.amount || form.amount <= 0) return toast.error("Montant invalide");
    setSaving(true);
    try {
      await axios.post(`${API}/finance/invoices`, form);
      toast.success("Facture créée");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="finance-invoice-dialog">
        <DialogHeader><DialogTitle>Nouvelle facture</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {clients.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
              Aucun client n'est marqué « payant ». Activez « Client payant » dans la configuration avant de créer une facture.
            </div>
          )}
          <div>
            <Label className="text-xs text-neutral-500">Client</Label>
            <Select value={form.client_id} onValueChange={(v) => set('client_id', v)}>
              <SelectTrigger data-testid="inv-client"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
              <SelectContent>
                {clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-neutral-500">Mois (YYYY-MM)</Label>
              <Input type="month" value={form.month} onChange={(e) => set('month', e.target.value)} data-testid="inv-month" />
            </div>
            <div>
              <Label className="text-xs text-neutral-500">Type</Label>
              <Select value={form.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger data-testid="inv-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-neutral-500">Libellé (description sur facture)</Label>
            <Input value={form.label} onChange={(e) => set('label', e.target.value)} placeholder="ex. Refonte site vitrine" data-testid="inv-label" />
          </div>
          <div>
            <Label className="text-xs text-neutral-500">Montant HT</Label>
            <div className="relative">
              <Input type="number" min="0" step="50" value={form.amount} onChange={(e) => set('amount', parseFloat(e.target.value) || 0)} className="pr-8" data-testid="inv-amount" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm">€</span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving || !form.client_id} data-testid="inv-save">{saving ? 'Enregistrement…' : 'Créer la facture'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
